/// <reference types="@angular/localize" />

import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular-ivy';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

// Initialize Sentry BEFORE bootstrap so it can capture errors thrown during
// module construction. Empty DSN (local dev) is a no-op.
if ((environment as { sentry?: { dsn?: string } }).sentry?.dsn) {
  // Release format: em3-registration@<package.version>. The version reads
  // from package.json at build time. The Azure DevOps prod pipeline mutates
  // package.json BEFORE build to append `-build.<Build.BuildId>` (e.g.
  // 1.0.0-build.12345), so each pipeline run produces a unique release.
  // Local builds keep the unmodified package.json version.
  const sentryCfg = (environment as { sentry: {
    dsn: string;
    environment: string;
    tracesSampleRate: number;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
  } }).sentry;
  Sentry.init({
    dsn: sentryCfg.dsn,
    environment: sentryCfg.environment,
    release: `em3-registration@${(environment as { appVersion: string }).appVersion}`,
    tracesSampleRate: sentryCfg.tracesSampleRate,
    // Session Replay sample rates. Random session sampling is off (free tier
    // is metered separately at 50 replays/mo). On-error rate is 1.0 so every
    // error gets the ~60s of session context leading up to it.
    replaysSessionSampleRate: sentryCfg.replaysSessionSampleRate,
    replaysOnErrorSampleRate: sentryCfg.replaysOnErrorSampleRate,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.routingInstrumentation,
        tracePropagationTargets: [/^\//, /^https:\/\/(dev-|staging-)?api-em3\.cufi\.org/],
      }),
      // Mask all text + media in replays. em3.registration captures attendee
      // PII (names, emails, addresses) during sign-up; none of that should
      // leave the browser to Sentry.
      new Sentry.Replay({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  });
}

if (environment.production) {
  enableProdMode();
}

platformBrowserDynamic().bootstrapModule(AppModule);
